import { z } from 'zod'

import { AddressSchema } from './address'
import { CaptureRefSchema } from './capture'
import { CustomFieldValueSchema } from './custom-field.types'
import { RecordStateSchema } from './merge'

// =====================
// Lifecycle stage (Path C — account-level funnel position)
// =====================
// Orthogonal to entityType (what kind of company: Client, Vendor, ...) and
// the legacy status column. A prospect Entity is a company we're talking to
// but haven't sold yet — it can have Contacts, Deals, notes, communications,
// but should NOT (by UX policy, not schema constraint) have Projects, Client
// Portal, Contracts, or Invoices until promoted to the active lifecycle.
export enum EntityLifecycleStageEnum {
  PROSPECT = 'prospect',
  ACTIVE_CUSTOMER = 'active_customer',
  AT_RISK = 'at_risk',
  CHURNED = 'churned',
  ARCHIVED = 'archived',
}

export const ENTITY_LIFECYCLE_STAGE_COLORS: Record<
  EntityLifecycleStageEnum,
  string
> = {
  [EntityLifecycleStageEnum.PROSPECT]: '#f59e0b',
  [EntityLifecycleStageEnum.ACTIVE_CUSTOMER]: '#1ead5b',
  [EntityLifecycleStageEnum.AT_RISK]: '#ef4444',
  [EntityLifecycleStageEnum.CHURNED]: '#6b7280',
  [EntityLifecycleStageEnum.ARCHIVED]: '#9a9a9e',
}

export const ENTITY_LIFECYCLE_STAGE_DISPLAY_NAMES: Record<
  EntityLifecycleStageEnum,
  string
> = {
  [EntityLifecycleStageEnum.PROSPECT]: 'Prospect',
  [EntityLifecycleStageEnum.ACTIVE_CUSTOMER]: 'Active',
  [EntityLifecycleStageEnum.AT_RISK]: 'At Risk',
  [EntityLifecycleStageEnum.CHURNED]: 'Churned',
  [EntityLifecycleStageEnum.ARCHIVED]: 'Archived',
}

// Lifecycle stages that gate access to the agency machinery (Projects, Client
// Portal, Contracts, Invoices). UX uses this list to soft-gate create flows.
export const ENTITY_CUSTOMER_STAGES: EntityLifecycleStageEnum[] = [
  EntityLifecycleStageEnum.ACTIVE_CUSTOMER,
  EntityLifecycleStageEnum.AT_RISK,
]

export function isCustomerLifecycleStage(stage: string): boolean {
  return ENTITY_CUSTOMER_STAGES.includes(stage as EntityLifecycleStageEnum)
}

// Entity sort field enum
export enum EntitySortFieldEnum {
  NAME = 'name',
  EMAIL = 'email',
  LIFECYCLE_STAGE = 'lifecycleStage',
  HEALTH_SCORE = 'healthScore',
  HEALTH_STATUS = 'healthStatus',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  HEALTH_CALCULATED_AT = 'healthCalculatedAt',
}

// Entity Type reference schema (for relation)
export const EntityTypeRefSchema = z
  .object({
    id: z.string(),
    typeId: z.string(),
    name: z.string(),
    color: z.string(),
  })
  .optional()
  .nullable()

// Lightweight EntityMember shape returned alongside Entity for avatar/listing
// rendering. Full management goes through the entity-members.api.
const EntityMemberRefSchema = z.object({
  id: z.string(),
  userId: z.string(),
  roleKey: z.string(),
  status: z.string().optional(),
  createdAt: z.date().optional(),
  user: z
    .object({
      id: z.string(),
      displayName: z.string().nullable().optional(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      email: z.string().optional(),
      image: z.string().nullable().optional(),
    })
    .optional(),
})

// Entity schema
export const EntitySchema = z.object({
  id: z.string(),
  name: z.string(),
  // Stores the org-specific CRM stage id for ENTITY_LIFECYCLE.
  lifecycleStage: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  organizationId: z.string(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  website: z.string().url().optional(),
  address: AddressSchema.optional(),
  logoUrl: z.string().optional(),
  // Time logged for this client is billable by default (null = org default).
  defaultBillable: z.boolean().nullable().optional(),
  entityTypeId: z.string().optional().nullable(),
  rateCardId: z.string().uuid().optional().nullable(),
  entityType: EntityTypeRefSchema,
  customFields: z.record(z.string(), CustomFieldValueSchema).optional(),
  settings: z.any().optional(),
  healthScore: z.number().nullable().optional(),
  healthComputedScore: z.number().nullable().optional(),
  healthStatus: z.enum(['healthy', 'watch', 'risk']).nullable().optional(),
  relationshipHealthScore: z.number().nullable().optional(),
  deliveryHealthScore: z.number().nullable().optional(),
  commercialHealthScore: z.number().nullable().optional(),
  healthSignals: z.record(z.string(), z.any()).optional(),
  healthCalculatedAt: z.date().nullable().optional(),
  // T2/T3 — record lifecycle (orthogonal to the funnel lifecycleStage).
  // Server-managed: 'merged' set by the merge engine (T3); mergedIntoId/mergedAt
  // let a tombstone render its "Merged into X" redirect banner (U2).
  recordState: RecordStateSchema.optional(),
  mergedIntoId: z.string().uuid().nullable().optional(),
  mergedAt: z.date().nullable().optional(),
  // T1 — capture provenance (U7 badge). Server-managed, write-once at
  // ingestion; null for records created before T1 or outside a wired producer.
  captureId: z.string().uuid().nullable().optional(),
  capture: CaptureRefSchema.nullable().optional(),
  members: z.array(EntityMemberRefSchema).optional(),
  _count: z
    .object({
      projects: z.number(),
    })
    .optional(),
})

export type Entity = z.infer<typeof EntitySchema>

// Create entity schema
export const CreateEntitySchema = EntitySchema.omit({
  id: true,
  organizationId: true,
  address: true,
  createdAt: true,
  updatedAt: true,
  entityType: true, // Don't include the relation in create
  healthScore: true,
  healthComputedScore: true,
  healthStatus: true,
  relationshipHealthScore: true,
  deliveryHealthScore: true,
  commercialHealthScore: true,
  healthSignals: true,
  healthCalculatedAt: true,
  recordState: true, // Server-managed lifecycle (T2/T3) — never client-set
  mergedIntoId: true,
  mergedAt: true,
  captureId: true, // Server-managed provenance (T1) — never client-set
  capture: true,
}).extend({
  address: z.any().optional(),
  entityTypeId: z.string().optional().nullable(),
  customFields: z.record(z.string(), CustomFieldValueSchema).optional(),
})

export type CreateEntity = z.infer<typeof CreateEntitySchema>

// Update entity schema
export const UpdateEntitySchema = CreateEntitySchema.partial().extend({
  customFields: z.record(z.string(), CustomFieldValueSchema).optional(),
})

export type UpdateEntity = z.infer<typeof UpdateEntitySchema>

// Entity response schema
export const EntityResponseSchema = z.object({
  data: EntitySchema,
  message: z.string().optional(),
})

export type EntityResponse = z.infer<typeof EntityResponseSchema>

// Entities response schema
export const EntitiesResponseSchema = z.object({
  data: z.array(EntitySchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type EntitiesResponse = z.infer<typeof EntitiesResponseSchema>
